import React, { useMemo, useRef, useLayoutEffect, useState } from 'react';
import { styled } from '@material-ui/core/styles';
import type { IVideoTrack } from '../../types';
import useMediaStreamTrack from '../../hooks/useMediaStreamTrack/useMediaStreamTrack';

const Video = styled('video')({
  width: '100%',
  height: '100%',
});

interface VideoTrackProps {
  track: IVideoTrack;
  isLocal?: boolean;
  priority?: unknown;
}

function getTrackName(track: any): string {
  return track.trackName ?? track.name ?? '';
}

export default function VideoTrack({ track, isLocal }: VideoTrackProps) {
  const ref = useRef<HTMLVideoElement>(null!);
  const mediaStreamTrack = useMediaStreamTrack(track);
  const [screenFitMode, setScreenFitMode] = useState<'contain' | 'cover'>('contain');

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !track) return;
    // Mobile (especially iOS Safari) requires inline playback attributes for autoplaying
    // RTC video elements. Muted helps autoplay policies.
    el.muted = true;
    el.autoplay = true;
    (el as any).playsInline = true;
    el.setAttribute('playsinline', 'true');
    el.setAttribute('webkit-playsinline', 'true');
    if (typeof track.play === 'function') {
      track.play(el);
    } else if (typeof (track as any).attach === 'function') {
      (track as any).attach(el);
    }
    return () => {
      // Only stop playback / detach from the element. Never call close() here — that destroys
      // local camera/mic tracks and causes black video when the component re-renders.
      if (typeof (track as any).stop === 'function') {
        try {
          (track as any).stop();
        } catch {
          /* ignore */
        }
      }
      el.srcObject = null;
    };
  }, [track]);

  const trackName = getTrackName(track);
  const isScreenShare = useMemo(() => trackName.includes('screen'), [trackName]);
  const isFrontFacing = mediaStreamTrack?.getSettings?.()?.facingMode !== 'environment';
  const style = {
    transform: isLocal && isFrontFacing ? 'scaleX(-1)' : '',
    objectFit: isScreenShare ? (screenFitMode as 'contain' | 'cover') : ('cover' as const),
  };

  return (
    <Video
      ref={ref}
      style={style}
      autoPlay
      playsInline
      muted
      // Avoid PiP UI overlays on some mobile browsers.
      disablePictureInPicture
      // Simple, discoverable zoom-like behavior for screen share:
      // double-click toggles between Fit (contain) and Fill (cover).
      onDoubleClick={() => {
        if (!isScreenShare) return;
        setScreenFitMode(m => (m === 'contain' ? 'cover' : 'contain'));
      }}
    />
  );
}
